import React, { useCallback, useState } from 'react';
import { BoundingBox } from '../../domain/valueObjects/BoundingBox';
import Map from 'react-map-gl';
import type { GeoJSONSource, MapRef } from 'react-map-gl';
import { STREET_VIEW_STYLE } from '../../../../utility/constants';
import MarkerClusterSource from './MarkerClusterSource';
import { useMapStore } from '../../application/mapStore';
import { MapOverlayTools } from './MapOverlayTools';
import { LassoDrawControl } from './LassoDrawControl';
import { LassoControlPanel } from './LassoControlPanel';
import { LassoPolygonSource } from './LassoPolygonSource';
import * as layersUtil from '../../../../components/property-map-viewer/MapLayersUtil';
import { usePropertyPopup } from '../hooks/usePropertyPopup';
import { renderPropertyPopup } from '../../infrastructure/adapters/mapboxPopupAdapter';
import { useMarkerHoverSync } from '../hooks/useMarkerHoverSync';
import { LoadingSpinner } from '../../../../components/icons/OurIcons';

export default function PublicMap({ mapKey }: { mapKey: string }) {
  const [mapNode, setMapNode] = useState(null);
  const { setBounds, bounds } = useMapStore();
  const {
    popupInfo,
    popupHeight,
    popupLoading,
    openPopup,
    closePopup,
    setPopupHeight,
    setPopupLoading,
    markerRef,
    markersRef,
  } = usePropertyPopup();

  const mapRef = useCallback((node: MapRef) => {
    if (node !== null) {
      setMapNode(node);
    }
  }, []);

  // Sync hover state between list views and individual map markers
  useMarkerHoverSync(markersRef);

  const handleBoundsChange = () => {
    if (!mapNode) {
      return;
    }
    const bounds = mapNode.getBounds();
    setBounds(
      new BoundingBox(
        bounds.getSouthWest().lat,
        bounds.getSouthWest().lng,
        bounds.getNorthEast().lat,
        bounds.getNorthEast().lng
      )
    );
  };

  function handleMapClick(e) {
    const features = e.features || [];
    const sourceId = 'public-markers';
    const clusterFeature = features.find((f) => f.properties?.cluster_id);
    if (!clusterFeature) return;

    const clusterId = clusterFeature.properties.cluster_id;
    const coordinates = clusterFeature.geometry.coordinates;

    const source = mapNode.getMap().getSource(sourceId) as GeoJSONSource;
    if (!source) return;

    source.getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err) return;
      mapNode.getMap().easeTo({ center: coordinates, zoom, duration: 500 });
    });
  }

  if (!mapKey) {
    return <LoadingSpinner />;
  }

  return (
    <Map
      ref={mapRef}
      mapboxAccessToken={mapKey}
      initialViewState={{
        bounds: bounds.toLngLatBounds(),
        fitBoundsOptions: { padding: 20 },
      }}
      mapStyle={STREET_VIEW_STYLE}
      onZoomEnd={handleBoundsChange}
      onDragEnd={handleBoundsChange}
      onRotateEnd={handleBoundsChange}
      onClick={(e) => handleMapClick(e)}
      interactiveLayerIds={[layersUtil.clusterLayer.id]}
    >
      {mapNode && (
        <>
          <MapOverlayTools mapNode={mapNode.getMap()} />
          <LassoControlPanel />
          <LassoDrawControl />
          <LassoPolygonSource />
        </>
      )}

      {/* Lasso / freehand polygon need to be copied over from the properties map */}
      <MarkerClusterSource
        id="public-markers"
        clusterMaxZoom={12}
        clusterRadius={50}
        onMarkerClick={openPopup}
        markersRef={markersRef}
      />

      {mapNode &&
        popupInfo &&
        markerRef &&
        renderPropertyPopup({
          popupInfo,
          popupHeight,
          mapNode,
          markerRef,
          onClose: closePopup,
          setPopupHeight,
          setPopupLoading,
        })}
    </Map>
  );
}
